import { ClickEvent, DataDisplayEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, Label, MapVendor, Panel, SelectionEvent, Snackbar, Tab, TableRow, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { AutogenLayoutDetentionHistory } from "./autogen/AutogenLayoutDetentionHistory";
import { ModelRow, ModelSearchResult, Navigation, UrlUtil } from "@mcleod/core";
import { DataLoadActionHandler, DataLoadActionType } from "@mcleod/common";
import { ModelBilling } from "@mcleod/ar";
import { DetentionUtil } from "./DetentionUtil";

export class DetentionHistory extends AutogenLayoutDetentionHistory {

    private isNoEditHistStatus: boolean = false;

    onLoad(): void | Promise<void> {
        this.styleTab(this.tabChargesAndPay);
        this.tabChargesAndPay.expanded = true;
    }

    labelStatusOnDataDisplay(event: DataDisplayEvent) {
        const label = event.target as Label;
        const modelRow = event.rowData;
        if (modelRow) {
            const status = modelRow.getString("status");
            DetentionUtil.configureIconByDetentionStatus(status, label);
            this.textboxDetentionTime.caption = ["A", "F", "B"].includes(status)
                                ? "Detention Accumulating"
                                : "Total Detention";
        }
    }

    panelMovementIdOnClick(event: ClickEvent) {
        if (this.sourceDetentionHistory.mode !== DataSourceMode.SEARCH) {
            const movementId = this.activeRow.getString('movement_id');
            const orderId = this.activeRow.getString('order_id');
            Navigation.navigateTo(`lme/dispatch/OrderTabView?mode=update&key=${movementId}&orderId=${orderId}`, { newTab: true });
        }
    }

    panelOrderIdOnClick(event: ClickEvent) {
        if (this.sourceDetentionHistory.mode !== DataSourceMode.SEARCH) {
            const orderId = this.activeRow.getString('order_id');
            Navigation.navigateTo(`lme/dispatch/OrderTabView?mode=update&key=${orderId}`, { newTab: true });
        }
    }

    sourceDetentionHistoryBeforeExecution(event: DataSourceExecutionEvent) {
        if (DataSourceAction.UPDATE === event.getAction()) {
            [this.sourceDetentionHistChg, this.sourceDetentionHistPay].forEach((source) => {
                source.data.filter(row => {
                    if (!row.hasChanged()) {
                        return false;
                    }
                    const changes = row.getChangedData();
                    return "units" in changes || "rate" in changes;
                }).forEach(row => row.set("manual_edit", true));
            });
        }
    }

    sourceDetentionHistoryAfterModeChange(event: DataSourceModeChangeEvent) {
        const newMode = event.newMode;
        const isSearch = DataSourceMode.SEARCH === newMode;
        this.styleForMode(isSearch);

        if (DataSourceMode.UPDATE === newMode) {
            if ("D" !== this.activeRow?.getString("status", null)) {
                [this.textboxAuthorizationNum, this.textboxAuthorizationStatus, this.textboxPayApproval, this.switchBillingApproved]
                    .forEach((component) => {
                        if (component) {
                            component.enabled = false;
                        }
                    });
                this.isNoEditHistStatus = true;
            }
        }
    }

    sourceDetentionHistoryAfterExecution(event: DataSourceExecutionEvent) {
        this.textboxPayApproval.enabled = ("R" === this.activeRow?.getString("detention_def.carrier_pay_option", null)
            || this.sourceDetentionHistPay.data.some((row: ModelRow) => "R" === row.getString("drs_payee.pay_option", null)));
        this.setupTrackingHistory();
    }

    setupTrackingHistory() {
        const tabsetHeaderRow = this.tabsetDetentionHist["_tabRow"];
        tabsetHeaderRow.visible = !this.activeRow.getBoolean("movement.brokerage", false);
        if (tabsetHeaderRow.visible) {
            const moveId = this.activeRow?.get("movement_id", null);
            const driverId = this.activeRow.getString("driver_id");
            this.layoutMovementMap.padding = 0;
            this.layoutMovementMap.mapStatus.height = undefined;
            this.layoutTrackingHistory.tablePositions.maxHeight = null;
            this.layoutTrackingHistory.tablePositions.fillHeight = true;
            this.layoutTrackingHistory.tableCallins.maxHeight = null;
            this.layoutTrackingHistory.tableCallins.fillHeight = true;

            this.layoutTrackingHistory.switchToMessages();
            this.layoutTrackingHistory.busy = true;
            this.layoutTrackingHistory.mainDataSource.search({
                movement_id: moveId,
                driver_id: driverId,
                hide_trailers: true,
            }).then(_ => this.layoutTrackingHistory.busy = false);

            this.searchMovementMap(moveId);
        }
    }

    searchMovementMap(moveId?: string) {
        if (moveId == null) {
            moveId = this.activeRow?.get("movement_id", null);
        }

        this.layoutMovementMap.search({
            movement_id: moveId,
            include_positions: !this.layoutTrackingHistory.switchPositionCallin.checked
        })
    }

    tableChargesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data as ModelRow;
        const overrideStatus = this.checkNoEditStatus(modelRow);
        this.setPrintableFromStatus(overrideStatus, tableRow);
        const imageChgManualEdit = tableRow.findComponentById("imageChgManualEdit");
        if (imageChgManualEdit) {
            imageChgManualEdit.visible = modelRow.getBoolean("manual_edit", false);
        }
    }

    tablePayOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const modelRow = tableRow.data as ModelRow;
        const overrideStatus = this.checkNoEditStatus(modelRow);
        this.setPrintableFromStatus(overrideStatus, tableRow);
        const imagePayManualEdit = tableRow.findComponentById("imagePayManualEdit");
        if (imagePayManualEdit) {
            imagePayManualEdit.visible = modelRow.getBoolean("manual_edit", false);
        }
    }

    tabsetDetentionHistAfterTabSelection(event:SelectionEvent) {
        if (event.newSelection.id === "tabMCPositions") {
            if (this.layoutMovementMap.mapStatus.vendor === MapVendor.TRIMBLE) {
                this.layoutMovementMap.mapStatus.map.fitPins();
            }

            // Refresh call-ins & positions when switching to tracking tab
            const tractorId = this.activeRow?.get("tractor_id", null);
            const moveId = this.activeRow?.get("movement_id", null);
            this.layoutTrackingHistory.mainDataSource.search({
                movement_id: moveId,
                callin_id: tractorId,
                hide_trailers: true,
            });
        }
    }

    checkNoEditStatus(modelRow: ModelRow): boolean {
        return ["C", "V"].includes(modelRow.getString("status", null));
    }

    setPrintableFromStatus(overrideStatus: boolean, tableRow: TableRow) {
        if (this.isNoEditHistStatus || overrideStatus) {
            tableRow.forEveryChildComponent((child) => {
                if (child instanceof Textbox) {
                    child.printable = true;
                }
            });
        }
    }

    chargeBlur(event) {
        const tableRow = event.target.row as TableRow;
        const units = tableRow.data.get("units");
        const rate = tableRow.data.get("rate");
        const chargeAmount = units * rate;
        if (!isNaN(chargeAmount)) {
            tableRow.data.set("charge_amount", chargeAmount);
        } else {
            tableRow.data.set("charge_amount", null);
        }
        this.resolveManualEdit(tableRow, "imageChgManualEdit");
    }

    payBlur(event) {
        const tableRow = event.target.row as TableRow;
        const units = tableRow.data.get("units");
        const rate = tableRow.data.get("rate");
        const chargeAmount = units * rate;
        if (!isNaN(chargeAmount)) {
            tableRow.data.set("pay_amount", chargeAmount);
        } else {
            tableRow.data.set("pay_amount", null);
        }
        this.resolveManualEdit(tableRow, "imagePayManualEdit");
    }

    resolveManualEdit(tableRow: TableRow, iconId: string) {
        const modelRow = tableRow.data as ModelRow;
        if (modelRow.hasChanged() && !modelRow.originalData?.manual_edit) {
            const changes = modelRow.getChangedData();
            const manualEdit = ("units" in changes || "rate" in changes);
            modelRow.set("manual_edit", manualEdit);
            const icon = tableRow.findComponentById(iconId);
            if (icon) {
                icon.visible = manualEdit;
            }
        }
    }

    async buttonChgDetailOnClick(event: ClickEvent) {
        const order_id = this.activeRow?.getString("order_id", null);
        if (order_id != null) {
            const result: ModelSearchResult = await new ModelBilling().search({ order_id: order_id })
            if (result.modelRows.length === 0) {
                Snackbar.showWarningSnackbar("No Billing records found for this Order.");
            } else {
                for (const row of result.modelRows) {
                    const billing_id = row.getString("id", null);
                    await Navigation.navigateTo(`lme/ar/ViewBilling?mode=update&key=${billing_id}`, { newTab: true });
                }
            }
        }
    }

    buttonPayDetailOnClick(event: ClickEvent) {
        const order_id = this.activeRow?.getString("order_id", null);
        if (order_id != null) {
            const dlaHandlers = [];
            const map = new Map<string, string>();
            map.set("filterValue", "order_id=" + order_id);
            dlaHandlers.push(DataLoadActionHandler.createUrlQueryParamString(DataLoadActionType.FILTER_TABLE, "tableSettlements", 1, map));

            Navigation.navigateTo("lme/settlement/SettlementList" + UrlUtil.buildQueryString(dlaHandlers), { newTab: true });
        }
    }

    buttonRecalculateOnClick(event: ClickEvent) {
        this.sourceDetentionHistory.search({id: this.activeRow?.getString("id")});
    }

    private getStatusColor(status: string): string {
        switch (status) {
            case "F":
                return "caution";
            case "B":
                return "error"
            case "D":
                return "success";
            case "C":
                return "subtle";
            default:
                return "default.reverse";
        }
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.setProps({ color: "subtle.light", paddingLeft: 10, borderWidth: 0 });
            tab.headingLabel.setProps({ color: "subtle.darker", fontSize: "large", fontBold: true })
        }
    }

    styleForMode(isSearch: boolean) {
        this.labelTimeZone.visible = !isSearch;
        if (isSearch) {
            this.panelOverview.forEveryChildComponent((child) => {
                if (child instanceof Panel) {
                    child.backgroundColor = "backgroundSubtle";
                    child.color = "default";
                }
                if (child instanceof Textbox) {
                    if (child.color === "primary") {
                        child.color = "default";
                    }
                    if (child.fontSize === "xxlarge") {
                        child.fontSize = null;
                    }
                    if (child.fontBold) {
                        child.fontBold = false;
                    }
                }
                if (child instanceof Label) {
                    child.color = "subtle.darker";
                }
            })
        }
    }
}
